import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Stack, Typography } from "@mui/material";
import { getPartySaveErrorResource } from "Api/Mutations/Party/getPartySaveErrorResource";
import { usePartyMutation } from "Api/Mutations/Party/usePartyMutation";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { CopyToClipboard } from "Components/Shared/CopyToClipboard";
import {
  AddressUncommittedProvider,
  useAddressUncommittedTracker,
} from "Components/Shared/Inputs/Form/AddressUncommittedContext";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { PARTY_TYPE_TO_FORM_FIELDS } from "Components/Users/UserDetailPageContent/Components/PartyForm/utils";
import { StyledForm } from "Components/Users/UserDetailPageContent/Components/StyledForm";
import {
  createPartyValidationSchema,
  type PartyFormDataInput,
  type PartyFormData,
} from "Components/Users/schemas";
import type { PartyDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { partyDtoToFormData } from "Components/Users/UserDetailPageContent/Components/PartyForm/utils";
import { PartyIdentificationImagesViewer } from "Components/Shared/PartyIdentificationImages/PartyIdentificationImagesViewer";

const COMPONENT_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Party.Form;

const IDENTIFICATIONS_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Party.Form
    .PartyIdentification;

type PartyFormProps = {
  party: PartyDto;
  onCancel?: () => void;
  onSaved?: () => void;
};

export const PartyForm = ({
  party,
  party: {
    type,
    hasIdentificationsUploaded,
    lastName,
    publicID,
    clientIdentifier,
    addresses,
  },
  onCancel,
  onSaved,
}: PartyFormProps) => {
  const { t } = useResource();
  const { hasUncommittedAddress, uncommittedContextValue } =
    useAddressUncommittedTracker();

  const formMethods = useForm<PartyFormDataInput, unknown, PartyFormData>({
    resolver: zodResolver(createPartyValidationSchema(t, type)),
    defaultValues: partyDtoToFormData(party),
  });
  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = formMethods;

  const { mutate: updateParty, isPending } = usePartyMutation({
    onSuccess: ({ data }) => {
      reset(partyDtoToFormData(data));
      toast.success(t(COMPONENT_RESOURCES.SubmitSuccess));
      onSaved?.();
    },
    onError: (error: unknown) => {
      toast.error(
        t(getPartySaveErrorResource(error, COMPONENT_RESOURCES.SubmitError)),
      );
    },
  });

  const onSubmit = (data: PartyFormData) => {
    if (hasUncommittedAddress) {
      toast.error(t(Resources.Components.Shared.AddressInput.UncommittedQuery));
      return;
    }

    updateParty({
      publicID: data.publicID || "",
      data: {
        ...data,
        licenses: party.licenses, // Keep original licenses from API
        // Required by the DTO shape only — the server ignores them on updates.
        verifiedPhone: party.verifiedPhone,
        vatStatus: party.vatStatus,
      },
    });
  };

  const PartyFields = PARTY_TYPE_TO_FORM_FIELDS[type];

  const identificationsDownload = hasIdentificationsUploaded ? (
    <Box mb={5}>
      <PartyIdentificationImagesViewer
        fileNamePrefix={`${t(IDENTIFICATIONS_RESOURCES.IdentificationsDownloadPrefix)}${lastName || ""}`}
        partyPublicID={publicID || ""}
      />
    </Box>
  ) : (
    t(IDENTIFICATIONS_RESOURCES.NoIdentificationsUploaded)
  );

  return (
    <FormProvider {...formMethods}>
      <AddressUncommittedProvider value={uncommittedContextValue}>
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <PrimaryPaper>
            {clientIdentifier && (
              <Box mb={3}>
                <Typography fontWeight={400}>
                  {t(COMPONENT_RESOURCES.ClientIdentifier)}:
                </Typography>
                <Box ml={3} display="flex" alignItems="center" gap={1}>
                  <Typography variant="h6" fontWeight={500}>
                    {clientIdentifier}
                  </Typography>
                  <CopyToClipboard
                    text={clientIdentifier}
                    accessibilityLabel={t(
                      COMPONENT_RESOURCES.CopyClientIdentifier,
                    )}
                    successMessage={t(
                      COMPONENT_RESOURCES.CopyClientIdentifierSuccess,
                    )}
                  />
                </Box>
              </Box>
            )}
            <PartyFields
              control={control}
              addresses={addresses}
              additionalIdentificationsSlot={identificationsDownload}
              verifiedPhone={party.verifiedPhone}
            />
            <Stack direction="row" gap={2}>
              {onCancel && (
                <Button
                  sx={{ px: 4 }}
                  variant="text"
                  color="primary"
                  onClick={onCancel}
                  disabled={isPending}
                >
                  {t(Resources.Common.Cancel)}
                </Button>
              )}
              <PrimaryButton
                type="submit"
                fullWidth
                disabled={!isDirty}
                isLoading={isPending}
                variant="contained"
                color="primary"
              >
                {t(COMPONENT_RESOURCES.Save)}
              </PrimaryButton>
            </Stack>
          </PrimaryPaper>
        </StyledForm>
      </AddressUncommittedProvider>
    </FormProvider>
  );
};
